<template>
  <v-container max-width="100%" style="margin-top: 2%">
    <v-dialog v-model="dialogRelatorio" fullscreen max-width="100%" style="z-index: 8000">
      <DetalhesViagem
        ref="detalheViagem"
        :linhas="linhasSelect"
        :viagem="editedItem"
        :user="user"
        :token="token"
        :key="relatorioKey"
        @close="closeRelatorio"
      />
    </v-dialog>

    <v-container class="toolbar__container">
      <v-form ref="form" v-model="formValidado">
        <v-row>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="idEmpresaSelected"
              :items="empresas"
              item-text="razao_social"
              item-value="id_empresa"
              clearable
              :label="$tc('company.self', 1)"
              :rules="[(v) => !!v || $t('company.companyRequired')]"
              @change="onEmpresaSelectChanged"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <MultiplaEscolha
              :items="unidadesItens"
              v-bind:selected-items.sync="unidadesSelecionadas"
              text="descricao_unidade"
              value="id_unidade"
              :label="$t('nav.registration.units')"
              :max-items="2"
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="idLinhaSelected"
              :items="linhasSelect"
              item-text="nome_linha"
              item-value="id_linha"
              clearable
              multiple
              :label="$tc('line.self', 1)"
              :disabled="!idEmpresaSelected"
            />
          </v-col>
          </v-row>
          <v-row>
          <v-col cols="12" sm="4" md="3">
            <v-datetime-picker
              :label="$t('datetime.startDate')"
              v-model="dti"
              :time-picker-props="timeProps"
              :text-field-props="textFieldProps"
              :date-picker-props="dtiProps"
              time-format="HH:mm:ss"
              date-format="dd/MM/yyyy"
              :clear-text="$t('datetime.close')"
            >
              <template slot="dateIcon"><v-icon>mdi-calendar</v-icon></template>
              <template slot="timeIcon"><v-icon>mdi-clock</v-icon></template>
            </v-datetime-picker>
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-datetime-picker
              :label="$t('datetime.finalDate')"
              v-model="dtf"
              :time-picker-props="timeProps"
              :text-field-props="textFieldProps"
              :date-picker-props="dtfProps"
              time-format="HH:mm:ss"
              date-format="dd/MM/yyyy"
              :clear-text="$t('datetime.close')"
            >
              <template slot="dateIcon"><v-icon>mdi-calendar</v-icon></template>
              <template slot="timeIcon"><v-icon>mdi-clock</v-icon></template>
            </v-datetime-picker>
          </v-col>
          <v-col cols="12" sm="2" md="2" class="d-flex align-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  fab
                  color="primary darken-1"
                  class="white--text mr-2"
                  v-bind="attrs"
                  v-on="on"
                  :loading="loadingResumo"
                  @click="populaRelatorio"
                >
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('operation.search') }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  fab
                  color="primary darken-1"
                  class="white--text"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!hasKpis || loadingResumo"
                  :loading="loadingPdf"
                  @click="exportarAnalisePdf"
                >
                  <v-icon>mdi-file-pdf-box</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('relOperationalDeviations.exportPdf') }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-form>
    </v-container>

    <div v-if="loadingPdf" class="pdf-export-overlay">
      <div class="text-center white--text">
        <v-progress-circular indeterminate color="white" size="48" class="mb-3" />
        <div>{{ $t('relOperationalDeviations.exportingPdf') }}</div>
      </div>
    </div>

    <template v-if="hasKpis">
      <div ref="sectionBoardings" class="mt-4">
        <div class="topic-section-title mb-2">
          {{ $t('relOperationalDeviations.topics.boardings') }}
        </div>
        <div class="kpi-cards-row mb-2">
          <v-card
            v-for="card in boardingsKpiCards"
            :key="card.key"
            flat
            class="kpi-card pa-3"
            style="border-radius: 10px;"
            :color="activeKpiFilter === card.key ? 'primary lighten-5' : undefined"
            @click="onKpiClick(card)"
          >
            <div class="kpi-card__label subtitle-2" style="color: #4a4a4a">{{ card.label }}</div>
            <div class="text-h5 font-weight-bold" :style="{ color: card.color }">{{ card.value }}</div>
            <div v-if="card.pct != null" class="mt-1 text-caption" :style="{ color: card.color }">
              {{ card.pct }}%
            </div>
          </v-card>
        </div>
        <v-card ref="sectionChartsBoardings" class="mt-2" style="border-radius: 10px;">
          <v-card-title class="pb-0">
            <span class="subtitle-1" style="color: #4a4a4a">{{ $t('relOperationalDeviations.charts.byAuthorization') }}</span>
          </v-card-title>
          <v-card-text>
            <ChartBar
              v-if="authChartHasData"
              :chart-options="authChartOptions"
              :chart-data="authChartData"
              :height="120"
            />
            <div v-else class="pa-4 text-center grey--text">{{ $t('relOperationalDeviations.charts.empty') }}</div>
          </v-card-text>
        </v-card>
        <v-card
          ref="boardingsTableCard"
          class="mt-4"
          style="border-radius: 10px;"
          :class="{ 'table-card--highlighted': highlightedTableTarget === 'boardings' }"
        >
          <v-alert
            v-if="highlightedTableTarget === 'boardings' && activeTableFilterLabel"
            dense
            text
            type="info"
            class="mx-3 mt-3 mb-0"
          >
            {{ activeTableFilterLabel }}
            <template v-slot:append>
              <v-btn icon x-small class="table-filter-close-btn" @click.stop="clearTableHighlight">
                <v-icon small color="grey darken-3">mdi-close</v-icon>
              </v-btn>
            </template>
          </v-alert>
          <v-tabs v-model="embarqueSubTab" background-color="transparent">
            <v-tab>{{ $t('relOperationalDeviations.tabs.withoutTrip') }}</v-tab>
            <v-tab>{{ $t('relOperationalDeviations.tabs.outsideFence') }}</v-tab>
            <v-tab>{{ $t('relOperationalDeviations.tabs.byFence') }}</v-tab>
          </v-tabs>
          <v-tabs-items v-model="embarqueSubTab">
            <v-tab-item>
              <merus-data-table
                :key="'semviagem-' + boardingsSemViagemTableKey"
                cod-tabela="DIAGDESVIOSOPERACIONAIS__EMBARQUES_SEM_VIAGEM"
                :headers="headersSemViagem"
                :suppress-header-values="suppressHeadersSemViagem"
                :items="embarquesSemViagemDisplay"
                :loading="loadingSemViagem"
                :default-multi-sort="boardingsTableMultiSort"
                :item-class="insightEmbarqueRowClass"
                item-key="id_embarque"
                @update:headers="headersSemViagem = $event"
              >
                <template v-slot:toolbar>
                  <CadastroToolbar
                    :title="$t('relOperationalDeviations.tabs.withoutTrip')"
                    :token="token"
                    :items="embarquesSemViagem"
                    csv-title="desviosoperacionaisembarquessemviagem"
                    :csv-headers="headersSemViagem"
                    :pdf-title="$t('relOperationalDeviations.tabs.withoutTrip')"
                    :pdf-headers="headersSemViagem"
                    pdf-filename="desviosoperacionaisembarquessemviagem"
                    hide-search
                  />
                </template>
                <template v-slot:item.autorizado_extenso="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        :color="item.autorizado == 1 ? 'green' : item.autorizado == 2 || (item.autorizado != null && item.autorizado != 0 && item.autorizado != 1) ? 'orange' : 'red'"
                      >mdi-circle</v-icon>
                    </template>
                    <span>{{ autorizadoLabel(item) }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:item.associacao="{ item }">
                  {{ associacaoLabel(item.associacao) }}
                </template>
                <template v-slot:item.sem_posicoes_janela="{ item }">
                  <v-tooltip bottom v-if="Number(item.sem_posicoes_janela) === 1">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="orange darken-2" v-bind="attrs" v-on="on">mdi-alert</v-icon>
                    </template>
                    <span>{{ $t('relBoarding.noPositionsTooltip') }}</span>
                  </v-tooltip>
                </template>
              </merus-data-table>
            </v-tab-item>
            <v-tab-item>
              <merus-data-table
                :key="'foracerca-' + boardingsForaCercaTableKey"
                cod-tabela="DIAGDESVIOSOPERACIONAIS__EMBARQUES_FORA_CERCA"
                :headers="headersForaCerca"
                :suppress-header-values="suppressHeadersForaCerca"
                :items="embarquesForaCercaDisplay"
                :loading="loadingForaCerca"
                :default-multi-sort="boardingsTableMultiSort"
                :item-class="insightEmbarqueRowClass"
                item-key="id_embarque"
                @update:headers="headersForaCerca = $event"
              >
                <template v-slot:toolbar>
                  <CadastroToolbar
                    :title="$t('relOperationalDeviations.tabs.outsideFence')"
                    :token="token"
                    :items="embarquesForaCerca"
                    csv-title="desviosoperacionaisembarquesforacerca"
                    :csv-headers="headersForaCerca"
                    :pdf-title="$t('relOperationalDeviations.tabs.outsideFence')"
                    :pdf-headers="headersForaCerca"
                    pdf-filename="desviosoperacionaisembarquesforacerca"
                    hide-search
                  />
                </template>
                <template v-slot:item.autorizado_extenso="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        :color="item.autorizado == 1 ? 'green' : item.autorizado == 2 || (item.autorizado != null && item.autorizado != 0 && item.autorizado != 1) ? 'orange' : 'red'"
                      >mdi-circle</v-icon>
                    </template>
                    <span>{{ autorizadoLabel(item) }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:item.motivo_fora_cerca="{ item }">
                  {{ motivoForaCercaLabel(item.motivo_fora_cerca) }}
                </template>
                <template v-slot:item.associacao="{ item }">
                  {{ associacaoLabel(item.associacao) }}
                </template>
                <template v-slot:item.sem_posicoes_janela="{ item }">
                  <v-tooltip bottom v-if="Number(item.sem_posicoes_janela) === 1">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="orange darken-2" v-bind="attrs" v-on="on">mdi-alert</v-icon>
                    </template>
                    <span>{{ $t('relBoarding.noPositionsTooltip') }}</span>
                  </v-tooltip>
                </template>
              </merus-data-table>
            </v-tab-item>
            <v-tab-item>
              <v-container class="pb-0">
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <v-autocomplete
                      v-model="idCercaSelected"
                      :items="cercasSelect"
                      item-text="nome_cerca"
                      item-value="id_cerca"
                      clearable
                      :label="$t('relOperationalDeviations.filters.fence')"
                      @change="populaEmbarquesPorCerca"
                    />
                  </v-col>
                </v-row>
              </v-container>
              <merus-data-table
                :key="'porcerca-' + boardingsPorCercaTableKey"
                cod-tabela="DIAGDESVIOSOPERACIONAIS__EMBARQUES_POR_CERCA"
                :headers="headersPorCerca"
                :suppress-header-values="suppressHeadersPorCerca"
                :items="embarquesPorCercaDisplay"
                :loading="loadingPorCerca"
                :default-multi-sort="boardingsTableMultiSort"
                :item-class="insightEmbarqueRowClass"
                item-key="id_embarque"
                @update:headers="headersPorCerca = $event"
              >
                <template v-slot:toolbar>
                  <CadastroToolbar
                    :title="$t('relOperationalDeviations.tabs.byFence')"
                    :token="token"
                    :items="embarquesPorCerca"
                    csv-title="desviosoperacionaisembarquesporcerca"
                    :csv-headers="headersPorCerca"
                    :pdf-title="$t('relOperationalDeviations.tabs.byFence')"
                    :pdf-headers="headersPorCerca"
                    pdf-filename="desviosoperacionaisembarquesporcerca"
                    hide-search
                  />
                </template>
                <template v-slot:item.autorizado_extenso="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        :color="item.autorizado == 1 ? 'green' : item.autorizado == 2 || (item.autorizado != null && item.autorizado != 0 && item.autorizado != 1) ? 'orange' : 'red'"
                      >mdi-circle</v-icon>
                    </template>
                    <span>{{ autorizadoLabel(item) }}</span>
                  </v-tooltip>
                </template>
                <template v-slot:item.associacao="{ item }">
                  {{ associacaoLabel(item.associacao) }}
                </template>
                <template v-slot:item.sem_posicoes_janela="{ item }">
                  <v-tooltip bottom v-if="Number(item.sem_posicoes_janela) === 1">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="orange darken-2" v-bind="attrs" v-on="on">mdi-alert</v-icon>
                    </template>
                    <span>{{ $t('relBoarding.noPositionsTooltip') }}</span>
                  </v-tooltip>
                </template>
              </merus-data-table>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
        <v-card v-if="boardingsInsights.length" class="mt-4" style="border-radius: 10px;">
          <v-card-title class="pb-0">
            <span class="subtitle-1" style="color: #4a4a4a">{{ $t('relOperationalDeviations.insights.title') }}</span>
          </v-card-title>
          <v-card-text class="pb-2">
            <div class="insights-list">
              <div
                v-for="insight in boardingsInsights"
                :key="insight.key"
                class="insight-item"
                :class="[
                  `insight-item--${insight.severity}`,
                  {
                    'insight-item--active': isBoardingInsightActive(insight),
                    'insight-item--expandable': insightHasTableSelector(insight),
                  },
                ]"
                @click="onBoardingInsightClick(insight)"
              >
                <v-icon
                  :color="insight.color"
                  class="insight-item__icon"
                  :class="{ 'insight-item__icon--clickable': insightHasTableSelector(insight) }"
                  @click.stop="insightHasTableSelector(insight) && toggleBoardingsTableSelector(insight)"
                >{{ insight.icon }}</v-icon>
                <div
                  class="insight-item__body"
                  v-if="insightHasTableSelector(insight)"
                >
                  <div
                    class="insight-item__header"
                    @click="toggleBoardingsTableSelector(insight)"
                  >
                    <div class="subtitle-2 font-weight-medium mb-1">{{ insight.title }}</div>
                    <div class="text-body-2 insight-item__action">{{ insight.action }}</div>
                  </div>
                  <v-expand-transition>
                    <div
                      v-if="boardingsTableSelectorExpandedKey === insight.key"
                      class="fence-insight-selector"
                    >
                      <v-list
                        v-if="getBoardingTableOptionsForInsight(insight).length"
                        dense
                        class="fence-insight-list pa-0"
                      >
                        <v-list-item
                          v-for="table in getBoardingTableOptionsForInsight(insight)"
                          :key="`${insight.key}-${table.subTab}`"
                          class="fence-insight-item"
                          :class="{ 'fence-insight-item--active': isBoardingTableSelected(insight, table) }"
                          @click.stop="onBoardingTableSelect(insight, table)"
                        >
                          <v-list-item-icon class="mr-3 my-2">
                            <v-icon
                              small
                              :color="isBoardingTableSelected(insight, table) ? 'primary' : 'grey'"
                            >mdi-table</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-title class="subtitle-2">{{ table.label }}</v-list-item-title>
                            <v-list-item-subtitle>
                              {{ $t('relOperationalDeviations.insights.boardings.tableListStats', {
                                count: table.count,
                              }) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                      <div v-else class="fence-insight-empty text-body-2 grey--text">
                        {{ $t('relOperationalDeviations.insights.boardings.noTableResults') }}
                      </div>
                    </div>
                  </v-expand-transition>
                </div>
                <div v-else class="insight-item__content">
                  <div class="subtitle-2 font-weight-medium mb-1">{{ insight.title }}</div>
                  <div class="text-body-2 insight-item__action">{{ insight.action }}</div>
                </div>
                <v-btn
                  v-if="insightHasTableSelector(insight) && getBoardingTableOptionsForInsight(insight).length"
                  icon
                  x-small
                  class="insight-item__chevron-btn"
                  @click.stop="toggleBoardingsTableSelector(insight)"
                >
                  <v-icon small color="grey">
                    {{ boardingsTableSelectorExpandedKey === insight.key ? 'mdi-chevron-up' : 'mdi-chevron-right' }}
                  </v-icon>
                </v-btn>
                <v-icon
                  v-else
                  small
                  color="grey"
                  class="insight-item__chevron"
                >mdi-chevron-right</v-icon>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <div ref="sectionTrips" class="mt-6">
        <div class="topic-section-title mb-2">
          {{ $t('relOperationalDeviations.topics.trips') }}
        </div>
        <div class="kpi-cards-row mb-2">
          <v-card
            v-for="card in tripsKpiCards"
            :key="card.key"
            flat
            class="kpi-card pa-3"
            style="border-radius: 10px;"
            :color="activeKpiFilter === card.key ? 'primary lighten-5' : undefined"
            @click="onKpiClick(card)"
          >
            <div class="kpi-card__label subtitle-2" style="color: #4a4a4a">{{ card.label }}</div>
            <div class="text-h5 font-weight-bold" :style="{ color: card.color }">{{ card.value }}</div>
            <div v-if="card.pct != null" class="mt-1 text-caption" :style="{ color: card.color }">
              {{ card.pct }}%
            </div>
          </v-card>
        </div>
        <v-card ref="sectionChartsTrips" class="mt-2" style="border-radius: 10px;">
          <v-card-title class="pb-0">
            <span class="subtitle-1" style="color: #4a4a4a">{{ $t('relOperationalDeviations.charts.byInvalidReason') }}</span>
          </v-card-title>
          <v-card-text>
            <ChartBar
              v-if="motivoChartHasData"
              :chart-options="motivoChartOptions"
              :chart-data="motivoChartData"
              :height="120"
            />
            <div v-else class="pa-4 text-center grey--text">{{ $t('relOperationalDeviations.charts.empty') }}</div>
            <div class="invalid-reason-legend mt-3">
              <div
                v-for="item in invalidReasonLegendItems"
                :key="item.key"
                class="invalid-reason-legend__item"
              >
                {{ item.text }}
              </div>
            </div>
          </v-card-text>
        </v-card>
        <v-card
          ref="tripsTableCard"
          class="mt-4"
          style="border-radius: 10px;"
          :class="{ 'table-card--highlighted': highlightedTableTarget === 'trips' }"
        >
          <v-alert
            v-if="highlightedTableTarget === 'trips' && activeTableFilterLabel"
            dense
            text
            type="info"
            class="mx-3 mt-3 mb-0"
          >
            {{ activeTableFilterLabel }}
            <template v-slot:append>
              <v-btn icon x-small class="table-filter-close-btn" @click.stop="clearTableHighlight">
                <v-icon small color="grey darken-3">mdi-close</v-icon>
              </v-btn>
            </template>
          </v-alert>
          <merus-data-table
            :key="'invalidas-' + tripsTableKey"
            cod-tabela="DIAGDESVIOSOPERACIONAIS__VIAGENS_INVALIDAS"
            :headers="headersViagensInvalidas"
            :suppress-header-values="suppressHeadersViagensInvalidas"
            :items="viagensInvalidasDisplay"
            :loading="loadingInvalidas"
            :default-multi-sort="tripsTableMultiSort"
            :item-class="insightViagemRowClass"
            item-key="id_viagem_grid"
            @update:headers="headersViagensInvalidas = $event"
          >
            <template v-slot:toolbar>
              <CadastroToolbar
                :title="$t('relOperationalDeviations.tabs.invalidTrips')"
                :token="token"
                :items="viagensInvalidas"
                csv-title="desviosoperacionaisviagensinvalidas"
                :csv-headers="headersViagensInvalidas"
                :pdf-title="$t('relOperationalDeviations.tabs.invalidTrips')"
                :pdf-headers="headersViagensInvalidas"
                pdf-filename="desviosoperacionaisviagensinvalidas"
                hide-search
              />
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon small class="mr-2" color="orange" @click="abrirRelatorioViagem(item)">mdi-eye</v-icon>
            </template>
            <template v-slot:item.data_hora_invalidacao_extenso="{ item }">
              {{ formatDateTimeShort(item.data_hora_invalidacao_extenso) }}
            </template>
            <template v-slot:item.horario_saida_realizada_extenso="{ item }">
              {{ formatDateTimeShort(item.horario_saida_realizada_extenso) }}
            </template>
            <template v-slot:item.motivo_viagem_invalida="{ item }">
              {{ motivoInvalidaLabel(item.motivo_viagem_invalida) }}
            </template>
          </merus-data-table>
        </v-card>
        <v-card v-if="tripsInsights.length" class="mt-4" style="border-radius: 10px;">
          <v-card-title class="pb-0">
            <span class="subtitle-1" style="color: #4a4a4a">{{ $t('relOperationalDeviations.insights.title') }}</span>
          </v-card-title>
          <v-card-text class="pb-2">
            <div class="insights-list">
              <div
                v-for="insight in tripsInsights"
                :key="insight.key"
                class="insight-item"
                :class="[
                  `insight-item--${insight.severity}`,
                  { 'insight-item--active': activeInsightKey === insight.key },
                ]"
                @click="onInsightClick(insight)"
              >
                <v-icon :color="insight.color" class="insight-item__icon">{{ insight.icon }}</v-icon>
                <div class="insight-item__content">
                  <div class="subtitle-2 font-weight-medium mb-1">{{ insight.title }}</div>
                  <div class="text-body-2 insight-item__action">{{ insight.action }}</div>
                </div>
                <v-icon small color="grey" class="insight-item__chevron">mdi-chevron-right</v-icon>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>

      <div ref="sectionFences" class="mt-6">
        <div class="topic-section-title mb-2">
          {{ $t('relOperationalDeviations.topics.fences') }}
        </div>
        <div class="kpi-cards-row mb-2">
          <v-card
            v-for="card in fencesKpiCards"
            :key="card.key"
            flat
            class="kpi-card pa-3"
            style="border-radius: 10px;"
            :color="activeKpiFilter === card.key ? 'primary lighten-5' : undefined"
            @click="onKpiClick(card)"
          >
            <div class="kpi-card__label subtitle-2" style="color: #4a4a4a">{{ card.label }}</div>
            <div class="text-h5 font-weight-bold" :style="{ color: card.color }">{{ card.value }}</div>
            <div v-if="card.pct != null" class="mt-1 text-caption" :style="{ color: card.color }">
              {{ card.pct }}%
            </div>
          </v-card>
        </div>
        <v-card
          ref="fencesTableCard"
          class="mt-2"
          style="border-radius: 10px;"
          :class="{ 'table-card--highlighted': highlightedTableTarget === 'fences' }"
        >
          <v-alert
            v-if="highlightedTableTarget === 'fences' && activeTableFilterLabel"
            dense
            text
            type="info"
            class="mx-3 mt-3 mb-0"
          >
            {{ activeTableFilterLabel }}
            <template v-slot:append>
              <v-btn icon x-small class="table-filter-close-btn" @click.stop="clearTableHighlight">
                <v-icon small color="grey darken-3">mdi-close</v-icon>
              </v-btn>
            </template>
          </v-alert>
          <merus-data-table
            :key="'cercasnaopassadas-' + fencesTableKey"
            cod-tabela="DIAGDESVIOSOPERACIONAIS__CERCAS_NAO_PASSADAS"
            :headers="headersCercasNaoPassadas"
            :suppress-header-values="suppressHeadersCercasNaoPassadas"
            :items="cercasNaoPassadasDisplay"
            :loading="loadingCercasNaoPassadas"
            :default-multi-sort="fencesTableMultiSort"
            :item-class="insightFenceRowClass"
            item-key="id_cercas_nao_passadas"
            @update:headers="headersCercasNaoPassadas = $event"
          >
            <template v-slot:toolbar>
              <CadastroToolbar
                :title="$t('relOperationalDeviations.tabs.missedFences')"
                :token="token"
                :items="cercasNaoPassadas"
                csv-title="desviosoperacionaiscercasnaopassadas"
                :csv-headers="headersCercasNaoPassadas"
                :pdf-title="$t('relOperationalDeviations.tabs.missedFences')"
                :pdf-headers="headersCercasNaoPassadas"
                pdf-filename="desviosoperacionaiscercasnaopassadas"
                hide-search
              />
            </template>
            <template v-slot:item.pct_nao_passadas="{ item }">
              {{ item.pct_nao_passadas }}%
            </template>
          </merus-data-table>
        </v-card>
        <v-card v-if="fencesInsightsSectionVisible" class="mt-4" style="border-radius: 10px;">
          <v-card-title class="pb-0">
            <span class="subtitle-1" style="color: #4a4a4a">{{ $t('relOperationalDeviations.insights.title') }}</span>
          </v-card-title>
          <v-card-text class="pb-2">
            <div
              class="insight-item insight-item--warning insight-item--expandable"
              :class="{ 'insight-item--active': isFencesGeneralInsightActive }"
            >
              <v-icon
                color="#ef6c00"
                class="insight-item__icon"
                @click.stop="toggleFenceSelector"
              >mdi-alert</v-icon>
              <div class="insight-item__body">
                <div class="insight-item__header" @click="toggleFenceSelector">
                  <div v-if="fencesGeneralInsight" class="subtitle-2 font-weight-medium mb-1">
                    {{ fencesGeneralInsight.title }}
                  </div>
                  <div v-if="fencesGeneralInsight" class="text-body-2 insight-item__action">
                    {{ fencesGeneralInsight.action }}
                  </div>
                </div>
                <v-expand-transition>
                  <div
                    v-if="fenceSelectorExpanded"
                    class="fence-insight-selector"
                  >
                    <v-text-field
                      v-model="filtroBuscaCercaIntervencao"
                      clearable
                      dense
                      hide-details
                      append-icon="mdi-magnify"
                      class="fence-insight-search"
                      :label="$t('relOperationalDeviations.filters.fence')"
                    />
                    <v-list
                      v-if="fencesInsightOptions.length"
                      dense
                      class="fence-insight-list pa-0"
                    >
                      <v-list-item
                        v-for="fence in fencesInsightOptions"
                        :key="fence.id_cercas_nao_passadas"
                        class="fence-insight-item"
                        :class="{ 'fence-insight-item--active': isFenceInsightSelected(fence) }"
                        @click.stop="onFenceInsightSelect(fence)"
                      >
                        <v-list-item-icon class="mr-3 my-2">
                          <v-icon small :color="isFenceInsightSelected(fence) ? 'primary' : 'grey'">
                            mdi-map-marker-radius
                          </v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="subtitle-2">
                            {{ fence.nome_cerca }}
                            <span class="grey--text text--darken-1">· {{ fence.cod_linha }}</span>
                          </v-list-item-title>
                          <v-list-item-subtitle>
                            {{ $t('relOperationalDeviations.insights.fences.fenceListStats', {
                              pct: fence.pct_nao_passadas,
                              missed: fence.qtd_nao_passadas,
                              trips: fence.qtd_viagens,
                            }) }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                    <div v-else class="fence-insight-empty text-body-2 grey--text">
                      {{ $t('relOperationalDeviations.insights.fences.noFenceResults') }}
                    </div>
                  </div>
                </v-expand-transition>
              </div>
              <v-btn
                v-if="(cercasNaoPassadas || []).length"
                icon
                x-small
                class="insight-item__chevron-btn"
                @click.stop="toggleFenceSelector"
              >
                <v-icon small color="grey">
                  {{ fenceSelectorExpanded ? 'mdi-chevron-up' : 'mdi-chevron-right' }}
                </v-icon>
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </template>
  </v-container>
</template>

<script>
import { Bar as ChartBar } from 'vue-chartjs/legacy';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import apiController from '@/utils/apiController';
import DateUtils from '../utils/dateutils.js';
import MultiplaEscolha from './Selects/MultiplaEscolha.vue';
import MerusDataTable from './Tables/MerusDataTable.vue';
import CadastroToolbar from './Toolbars/CadastroToolbar.vue';
import DetalhesViagem from './DetalhesViagem.vue';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default {
  components: {
    ChartBar,
    MultiplaEscolha,
    MerusDataTable,
    CadastroToolbar,
    DetalhesViagem,
  },
  data: () => ({
    user: null,
    token: null,
    formValidado: false,
    config: null,
    dti: null,
    dtf: null,
    timeProps: { useSeconds: true },
    idEmpresaSelected: null,
    idLinhaSelected: [],
    unidadesSelecionadas: [],
    empresas: [],
    unidades: [],
    linhasSelect: [],
    cercasSelect: [],
    idCercaSelected: null,
    embarqueSubTab: 0,
    kpis: null,
    porAutorizado: [],
    porMotivoForaCerca: [],
    porMotivoInvalida: [],
    viagensInvalidas: [],
    embarquesForaCerca: [],
    embarquesPorCerca: [],
    embarquesSemViagem: [],
    cercasNaoPassadas: [],
    loadingResumo: false,
    loadingPdf: false,
    loadingInvalidas: false,
    loadingForaCerca: false,
    loadingPorCerca: false,
    loadingSemViagem: false,
    loadingCercasNaoPassadas: false,
    activeKpiFilter: null,
    activeInsightKey: null,
    highlightedTableTarget: null,
    activeTableFilterLabel: null,
    filtroMotivoInvalida: null,
    filtroEmbarqueAutorizado: null,
    filtroEmbarqueMotivoFora: null,
    filtroEmbarqueSemPosicoes: false,
    filtroCercaHighlight: null,
    filtroBuscaCercaIntervencao: '',
    fenceSelectorExpanded: false,
    boardingsTableSelectorExpandedKey: null,
    dialogRelatorio: false,
    editedItem: {},
    relatorioKey: 0,
    headersViagensInvalidas: [],
    headersForaCerca: [],
    headersPorCerca: [],
    headersSemViagem: [],
    headersCercasNaoPassadas: [],
  }),
  computed: {
    tableKey() {
      return `reldesvios-${this.$i18n.locale}`;
    },
    textFieldProps() {
      return {
        prependInnerIcon: 'mdi-calendar',
        rules: [(v) => !!v || this.$t('contract.messages.selectDate')],
      };
    },
    yesterday() {
      const td = new Date();
      td.setDate(td.getDate() - 1);
      return td.toISOString().substring(0, 10);
    },
    dtiProps() {
      return { max: this.yesterday };
    },
    dtfProps() {
      let min = null;
      if (this.dti) {
        if (typeof this.dti === 'string') {
          min = `${this.dti.substring(6, 10)}-${this.dti.substring(3, 5)}-${this.dti.substring(0, 2)}`;
        } else {
          min = this.dti.toISOString().substring(0, 10);
        }
      }
      return { min };
    },
    unidadesItens() {
      return this.unidades;
    },
    hasKpis() {
      return !!this.kpis;
    },
    empresaLabel() {
      const emp = (this.empresas || []).find((e) => e.id_empresa === this.idEmpresaSelected);
      return emp ? emp.razao_social : '';
    },
    kpiCards() {
      if (!this.kpis) return [];
      return [
        {
          key: 'total',
          topic: 'boardings',
          label: this.$t('relOperationalDeviations.kpis.totalBoardings'),
          value: this.kpis.total_embarques,
          color: '#1565c0',
          pct: null,
        },
        {
          key: 'embarques_viagem_valida',
          topic: 'boardings',
          label: this.$t('relOperationalDeviations.kpis.onValidTrip'),
          value: this.kpis.embarques_viagem_valida,
          color: '#2e7d32',
          pct: this.kpis.pct_embarques_viagem_valida,
        },
        {
          key: 'viagem_invalida',
          topic: 'boardings',
          label: this.$t('relOperationalDeviations.kpis.onInvalidTrip'),
          value: this.kpis.viagem_invalida,
          color: '#ad1457',
          pct: this.kpis.pct_viagem_invalida,
        },
        {
          key: 'sem_viagem',
          topic: 'boardings',
          embarqueSubTab: 0,
          label: this.$t('relOperationalDeviations.kpis.withoutTrip'),
          value: this.kpis.sem_viagem,
          color: '#c62828',
          pct: this.kpis.pct_sem_viagem,
        },
        {
          key: 'total_viagens',
          topic: 'trips',
          label: this.$t('relOperationalDeviations.kpis.totalTrips'),
          value: this.kpis.total_viagens,
          color: '#0277bd',
          pct: null,
        },
        {
          key: 'viagens_validas',
          topic: 'trips',
          label: this.$t('relOperationalDeviations.kpis.validTrips'),
          value: this.kpis.viagens_validas,
          color: '#388e3c',
          pct: this.kpis.pct_viagens_validas,
        },
        {
          key: 'viagens_invalidas',
          topic: 'trips',
          label: this.$t('relOperationalDeviations.kpis.invalidTrips'),
          value: this.kpis.viagens_invalidas,
          color: '#6a1b9a',
          pct: this.kpis.pct_viagens_invalidas,
        },
        {
          key: 'cercas_esperadas',
          topic: 'fences',
          label: this.$t('relOperationalDeviations.kpis.expectedFences'),
          value: this.kpis.cercas_esperadas,
          color: '#0288d1',
          pct: null,
        },
        {
          key: 'cercas_passadas',
          topic: 'fences',
          label: this.$t('relOperationalDeviations.kpis.passedFences'),
          value: this.kpis.cercas_passadas,
          color: '#2e7d32',
          pct: this.kpis.pct_cercas_passadas,
        },
        {
          key: 'cercas_nao_passadas',
          topic: 'fences',
          label: this.$t('relOperationalDeviations.kpis.missedFences'),
          value: this.kpis.cercas_nao_passadas,
          color: '#c62828',
          pct: this.kpis.pct_cercas_nao_passadas,
        },
        {
          key: 'viagens_com_cerca_nao_passada',
          topic: 'fences',
          label: this.$t('relOperationalDeviations.kpis.tripsWithMissedFence'),
          value: this.kpis.viagens_com_cerca_nao_passada,
          color: '#ad1457',
          pct: this.kpis.pct_viagens_com_cerca_nao_passada,
        },
        {
          key: 'cercas_distintas_nao_passadas',
          topic: 'fences',
          label: this.$t('relOperationalDeviations.kpis.distinctMissedFences'),
          value: this.kpis.cercas_distintas_nao_passadas,
          color: '#ef6c00',
          pct: null,
        },
      ];
    },
    boardingsKpiCards() {
      return this.kpiCards.filter((card) => card.topic === 'boardings');
    },
    tripsKpiCards() {
      return this.kpiCards.filter((card) => card.topic === 'trips');
    },
    fencesKpiCards() {
      return this.kpiCards.filter((card) => card.topic === 'fences');
    },
    operationalInsights() {
      if (!this.kpis) return [];

      const insights = [];
      const k = this.kpis;
      const push = (item) => insights.push(item);

      const severityMeta = {
        error: { color: '#c62828', icon: 'mdi-alert-circle' },
        warning: { color: '#ef6c00', icon: 'mdi-alert' },
        info: { color: '#1565c0', icon: 'mdi-information' },
      };
      const meta = (severity) => severityMeta[severity] || severityMeta.info;

      if (Number(k.sem_viagem) > 0) {
        push({
          key: 'boardings-without-trip',
          topic: 'boardings',
          severity: 'error',
          ...meta('error'),
          title: this.$t('relOperationalDeviations.insights.boardings.withoutTrip.title', { count: k.sem_viagem }),
          action: this.$t('relOperationalDeviations.insights.boardings.withoutTrip.action'),
          scrollTopic: 'boardings',
          tableTarget: 'boardings',
          embarqueSubTab: 0,
        });
      }

      if (Number(k.nao_autorizado) > 0) {
        push({
          key: 'boardings-not-authorized',
          topic: 'boardings',
          severity: 'warning',
          ...meta('warning'),
          title: this.$t('relOperationalDeviations.insights.boardings.notAuthorized.title', { count: k.nao_autorizado }),
          action: this.$t('relOperationalDeviations.insights.boardings.notAuthorized.action'),
          scrollTopic: 'boardings',
          tableTarget: 'boardings',
          filtroEmbarqueAutorizado: 0,
        });
      }

      if (Number(k.rota_errada) > 0) {
        push({
          key: 'boardings-wrong-route',
          topic: 'boardings',
          severity: 'warning',
          ...meta('warning'),
          title: this.$t('relOperationalDeviations.insights.boardings.wrongRoute.title', { count: k.rota_errada }),
          action: this.$t('relOperationalDeviations.insights.boardings.wrongRoute.action'),
          scrollTopic: 'boardings',
          tableTarget: 'boardings',
          filtroEmbarqueAutorizado: 'rota_errada',
        });
      }

      (this.porMotivoForaCerca || []).forEach((row) => {
        const count = Number(row.qtd) || 0;
        if (count <= 0) return;
        const motivo = row.motivo_fora_cerca;
        if (motivo === 'sem_cerca') {
          push({
            key: 'boardings-outside-no-fence',
            topic: 'boardings',
            severity: 'error',
            ...meta('error'),
            title: this.$t('relOperationalDeviations.insights.boardings.outsideFenceNoFence.title', { count }),
            action: this.$t('relOperationalDeviations.insights.boardings.outsideFenceNoFence.action'),
            scrollTopic: 'boardings',
            tableTarget: 'boardings',
            embarqueSubTab: 1,
            filtroEmbarqueMotivoFora: 'sem_cerca',
          });
        } else if (motivo === 'cerca_fora_rota') {
          push({
            key: 'boardings-outside-off-route',
            topic: 'boardings',
            severity: 'warning',
            ...meta('warning'),
            title: this.$t('relOperationalDeviations.insights.boardings.outsideFenceOffRoute.title', { count }),
            action: this.$t('relOperationalDeviations.insights.boardings.outsideFenceOffRoute.action'),
            scrollTopic: 'boardings',
            tableTarget: 'boardings',
            embarqueSubTab: 1,
            filtroEmbarqueMotivoFora: 'cerca_fora_rota',
          });
        }
      });

      const semPosicoes = this.countSemPosicoesJanela();
      if (semPosicoes > 0) {
        push({
          key: 'boardings-no-gps',
          topic: 'boardings',
          severity: 'warning',
          ...meta('warning'),
          title: this.$t('relOperationalDeviations.insights.boardings.noGpsPositions.title', { count: semPosicoes }),
          action: this.$t('relOperationalDeviations.insights.boardings.noGpsPositions.action'),
          scrollTopic: 'boardings',
          tableTarget: 'boardings',
          filtroEmbarqueSemPosicoes: true,
        });
      }

      const motivosInvalida = this.porMotivoInvalida || [];
      const motivosComQtd = motivosInvalida.filter((row) => Number(row.qtd) > 0);
      if (motivosComQtd.length) {
        motivosComQtd.forEach((row) => {
          const count = Number(row.qtd) || 0;
          const motivo = (row.motivo_viagem_invalida || 'SEM_MOTIVO').trim() || 'SEM_MOTIVO';
          let i18nKey = 'generic';
          if (motivo === 'MINIMO_CERCA_ANULA_VIAGEM') i18nKey = 'minimoCerca';
          else if (motivo === 'TEMPO_MEDIO_ANULA_VIAGEM') i18nKey = 'tempoMedio';

          push({
            key: `trips-invalid-${motivo}`,
            topic: 'trips',
            severity: 'error',
            ...meta('error'),
            title: this.$t(`relOperationalDeviations.insights.trips.${i18nKey}.title`, { count }),
            action: this.$t(`relOperationalDeviations.insights.trips.${i18nKey}.action`),
            scrollTopic: 'trips',
            tableTarget: 'trips',
            filtroMotivoInvalida: motivo,
          });
        });
      } else if (Number(k.viagens_invalidas) > 0) {
        push({
          key: 'trips-invalid-generic',
          topic: 'trips',
          severity: 'error',
          ...meta('error'),
          title: this.$t('relOperationalDeviations.insights.trips.generic.title', { count: k.viagens_invalidas }),
          action: this.$t('relOperationalDeviations.insights.trips.generic.action'),
          scrollTopic: 'trips',
          tableTarget: 'trips',
        });
      }

      if (Number(k.cercas_nao_passadas) > 0) {
        push({
          key: 'fences-missed',
          topic: 'fences',
          severity: 'warning',
          ...meta('warning'),
          title: this.$t('relOperationalDeviations.insights.fences.missedFences.title', {
            count: k.cercas_nao_passadas,
            pct: k.pct_cercas_nao_passadas,
          }),
          action: this.$t('relOperationalDeviations.insights.fences.missedFences.action'),
          scrollTopic: 'fences',
          tableTarget: 'fences',
        });
      }

      const severityOrder = { error: 0, warning: 1, info: 2 };
      return insights.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
    },
    boardingsInsights() {
      return this.operationalInsights.filter((insight) => insight.topic === 'boardings');
    },
    boardingsSemViagemTableKey() {
      return `${this.tableKey}-semviagem-${this.boardingsInsightFilterKey}-${this.activeInsightKey || 'none'}-${this.embarqueSubTab}`;
    },
    boardingsForaCercaTableKey() {
      return `${this.tableKey}-foracerca-${this.boardingsInsightFilterKey}-${this.activeInsightKey || 'none'}-${this.embarqueSubTab}`;
    },
    boardingsPorCercaTableKey() {
      return `${this.tableKey}-porcerca-${this.boardingsInsightFilterKey}-${this.activeInsightKey || 'none'}-${this.embarqueSubTab}`;
    },
    boardingsTableMultiSort() {
      const increasing = { text: this.$t('system.labels.increasing') || 'Asc', value: 1 };
      const decreasing = { text: this.$t('routs.decreasing') || 'Desc', value: -1 };
      if (!this.hasActiveEmbarqueInsightFilter()) {
        return [{ value: 'dt_embarque', options: decreasing }];
      }
      return [
        { value: '_sort_embarque_priority', options: increasing },
        { value: '_sort_embarque_group_key', options: increasing },
        { value: 'dt_embarque', options: decreasing },
      ];
    },
    embarquesSemViagemDisplay() {
      return this.buildEmbarquesDisplay(this.embarquesSemViagem);
    },
    embarquesForaCercaDisplay() {
      return this.buildEmbarquesDisplay(this.embarquesForaCerca);
    },
    embarquesPorCercaDisplay() {
      return this.buildEmbarquesDisplay(this.embarquesPorCerca);
    },
    tripsInsights() {
      return this.operationalInsights.filter((insight) => insight.topic === 'trips');
    },
    tripsTableKey() {
      const motivo = this.filtroMotivoInvalida || 'all';
      return `${this.tableKey}-${motivo}-${this.activeInsightKey || 'none'}`;
    },
    tripsTableMultiSort() {
      const increasing = { text: this.$t('system.labels.increasing') || 'Asc', value: 1 };
      const decreasing = { text: this.$t('routs.decreasing') || 'Desc', value: -1 };
      if (!this.filtroMotivoInvalida) {
        return [{ value: 'data_hora_invalidacao', options: decreasing }];
      }
      return [
        { value: '_sort_motivo_priority', options: increasing },
        { value: '_sort_motivo_key', options: increasing },
        { value: 'data_hora_invalidacao', options: decreasing },
      ];
    },
    viagensInvalidasDisplay() {
      const selected = this.filtroMotivoInvalida;
      if (!selected) {
        return this.viagensInvalidas || [];
      }

      const items = (this.viagensInvalidas || []).map((item) => {
        const motivo = this.normalizeMotivoInvalida(item.motivo_viagem_invalida);
        return {
          ...item,
          _sort_motivo_priority: motivo === selected ? 0 : 1,
          _sort_motivo_key: motivo,
        };
      });

      return items.sort((a, b) => {
        if (a._sort_motivo_priority !== b._sort_motivo_priority) {
          return a._sort_motivo_priority - b._sort_motivo_priority;
        }
        const motivoCmp = a._sort_motivo_key.localeCompare(b._sort_motivo_key);
        if (motivoCmp !== 0) return motivoCmp;
        return this.compareViagemInvalidacaoDesc(a, b);
      });
    },
    fencesGeneralInsight() {
      return this.operationalInsights.find((insight) => insight.key === 'fences-missed') || null;
    },
    fencesInsightOptions() {
      const query = this.filtroBuscaCercaIntervencao;
      return [...(this.cercasNaoPassadas || [])]
        .filter((item) => this.matchesFenceSearch(item, query))
        .sort((a, b) => Number(b.pct_nao_passadas) - Number(a.pct_nao_passadas));
    },
    fencesInsightsSectionVisible() {
      return !!this.fencesGeneralInsight || this.fencesInsightOptions.length > 0;
    },
    isFencesGeneralInsightActive() {
      return this.fenceSelectorExpanded || (this.highlightedTableTarget === 'fences' && !!this.activeInsightKey);
    },
    fencesTableKey() {
      const highlight = this.filtroCercaHighlight;
      const highlightKey = highlight ? `${highlight.id_cerca}-${highlight.id_linha}` : 'all';
      return `${this.tableKey}-${highlightKey}-${this.activeInsightKey || 'none'}`;
    },
    fencesTableMultiSort() {
      const increasing = { text: this.$t('system.labels.increasing') || 'Asc', value: 1 };
      const decreasing = { text: this.$t('routs.decreasing') || 'Desc', value: -1 };
      return [
        { value: '_sort_fence_priority', options: increasing },
        { value: 'qtd_nao_passadas', options: decreasing },
      ];
    },
    cercasNaoPassadasDisplay() {
      const highlight = this.filtroCercaHighlight;
      const items = (this.cercasNaoPassadas || []).map((item) => ({
        ...item,
        _sort_fence_priority:
          highlight &&
          Number(item.id_cerca) === Number(highlight.id_cerca) &&
          Number(item.id_linha) === Number(highlight.id_linha)
            ? 0
            : 1,
      }));

      return items.sort((a, b) => {
        if (a._sort_fence_priority !== b._sort_fence_priority) {
          return a._sort_fence_priority - b._sort_fence_priority;
        }
        return Number(b.qtd_nao_passadas) - Number(a.qtd_nao_passadas);
      });
    },
    authChartHasData() {
      return (this.porAutorizado || []).some((r) => Number(r.qtd) > 0);
    },
    motivoChartHasData() {
      return (this.porMotivoInvalida || []).some((r) => Number(r.qtd) > 0);
    },
    barChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        animation: false,
        plugins: {
          legend: { display: false },
          title: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { precision: 0 },
          },
        },
      };
    },
    authChartOptions() {
      return {
        ...this.barChartOptions,
        onClick: (_evt, elements) => {
          if (!elements || !elements.length) return;
          this.navigateToTopic('boardings', 1);
        },
      };
    },
    motivoChartOptions() {
      return {
        ...this.barChartOptions,
        onClick: (_evt, elements) => {
          if (!elements || !elements.length) return;
          const idx = elements[0].index;
          const row = this.porMotivoInvalida[idx];
          if (!row) return;
          const motivo = row.motivo_viagem_invalida || 'SEM_MOTIVO';
          if (this.filtroMotivoInvalida === motivo && this.highlightedTableTarget === 'trips') {
            this.clearTableHighlight();
            return;
          }
          this.activeInsightKey = `chart-trips-${motivo}`;
          this.highlightedTableTarget = 'trips';
          this.activeTableFilterLabel = this.motivoInvalidaLabel(motivo);
          this.filtroMotivoInvalida = motivo;
          this.scrollToTableRef('tripsTableCard');
        },
      };
    },
    authChartData() {
      const labels = (this.porAutorizado || []).map((r) => r.label);
      const data = (this.porAutorizado || []).map((r) => Number(r.qtd) || 0);
      return {
        labels,
        datasets: [
          {
            label: this.$t('relOperationalDeviations.charts.byAuthorization'),
            data,
            backgroundColor: ['#dc2626', '#16a34a', '#ca8a04'],
          },
        ],
      };
    },
    motivoChartData() {
      const labels = (this.porMotivoInvalida || []).map((r) =>
        this.motivoInvalidaLabel(r.motivo_viagem_invalida || 'SEM_MOTIVO')
      );
      const data = (this.porMotivoInvalida || []).map((r) => Number(r.qtd) || 0);
      return {
        labels,
        datasets: [
          {
            label: this.$t('relOperationalDeviations.charts.byInvalidReason'),
            data,
            backgroundColor: '#7c3aed',
          },
        ],
      };
    },
    invalidReasonLegendItems() {
      return [
        {
          key: 'MINIMO_CERCA_ANULA_VIAGEM',
          text: this.$t('relOperationalDeviations.invalidReasonLegends.MINIMO_CERCA_ANULA_VIAGEM'),
        },
        {
          key: 'TEMPO_MEDIO_ANULA_VIAGEM',
          text: this.$t('relOperationalDeviations.invalidReasonLegends.TEMPO_MEDIO_ANULA_VIAGEM'),
        },
      ];
    },
    defaultSortInvalidas() {
      return [{ value: 'data_hora_invalidacao', options: { text: this.$t('routs.decreasing') || 'Desc', value: -1 } }];
    },
    defaultSortEmbarques() {
      return [{ value: 'dt_embarque', options: { text: this.$t('routs.decreasing') || 'Desc', value: -1 } }];
    },
    defaultSortCercasNaoPassadas() {
      return [{ value: 'qtd_nao_passadas', options: { text: this.$t('routs.decreasing') || 'Desc', value: -1 } }];
    },
    suppressHeadersViagensInvalidas() {
      return ['nome_linha'];
    },
    suppressHeadersSemViagem() {
      return ['cod_cerca'];
    },
    suppressHeadersForaCerca() {
      return ['cod_cerca'];
    },
    suppressHeadersPorCerca() {
      const suppressed = ['cod_cerca'];
      if (this.idCercaSelected) {
        suppressed.push('nome_cerca');
      }
      return suppressed;
    },
    suppressHeadersCercasNaoPassadas() {
      return ['cod_cerca', 'nome_linha'];
    },
  },
  watch: {
    '$i18n.locale'() {
      this.initialize();
    },
  },
  created() {
    this.user = JSON.parse(sessionStorage.getItem('user'));
    this.token = sessionStorage.getItem('api');
    this.initializeHeaders();
    this.initialize();
  },
  methods: {
    initialize() {
      this.getConfig();
      this.populaEmpresas();
      this.populaUnidades();
      this.populaCercas();
    },
    initializeHeaders() {
      this.headersViagensInvalidas = [
        { text: this.$t('relOperationalDeviations.headers.line'), value: 'cod_linha', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.vehicle'), value: 'numero_ordem', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.invalidReason'), value: 'motivo_viagem_invalida', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.invalidatedAt'), value: 'data_hora_invalidacao_extenso', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.departureRealized'), value: 'horario_saida_realizada_extenso', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.boardings'), value: 'qtd_embarques', sortable: true, filtrable: false },
        { text: this.$t('relOperationalDeviations.headers.authorized'), value: 'qtd_autorizado', sortable: true, filtrable: false },
        { text: this.$t('relOperationalDeviations.headers.notAuthorized'), value: 'qtd_nao_autorizado', sortable: true, filtrable: false },
        { text: this.$t('relOperationalDeviations.headers.wrongRoute'), value: 'qtd_rota_errada', sortable: true, filtrable: false },
        { text: '', value: 'actions', sortable: false, filtrable: false },
      ];
      this.headersForaCerca = this.buildEmbarqueHeaders({ withMotivoFora: true });
      this.headersPorCerca = this.buildEmbarqueHeaders();
      this.headersSemViagem = this.buildEmbarqueHeaders();
      this.headersCercasNaoPassadas = [
        { text: this.$t('relOperationalDeviations.headers.fenceName'), value: 'nome_cerca', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.line'), value: 'cod_linha', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.fenceType'), value: 'descricao_tipo_cerca', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.tripsExpected'), value: 'qtd_viagens', sortable: true, filtrable: false },
        { text: this.$t('relOperationalDeviations.headers.fencesPassed'), value: 'qtd_passadas', sortable: true, filtrable: false },
        { text: this.$t('relOperationalDeviations.headers.fencesMissed'), value: 'qtd_nao_passadas', sortable: true, filtrable: false },
        { text: this.$t('relOperationalDeviations.headers.pctMissed'), value: 'pct_nao_passadas', sortable: true, filtrable: false },
      ];
    },
    buildEmbarqueHeaders(options = {}) {
      const { withMotivoFora = false, withAssociacao = true } = options;
      const headers = [
        { text: this.$t('datetime.date'), value: 'data_embarque', sortable: true, filtrable: true },
        { text: this.$t('datetime.time'), value: 'hora_embarque', sortable: true, filtrable: true },
        { text: this.$t('passengers.personal.registrationCode'), value: 'matricula', sortable: true, filtrable: true },
        { text: this.$tc('passenger.self', 1), value: 'nome_completo', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.fenceName'), value: 'nome_cerca', sortable: true, filtrable: true },
        { text: this.$tc('line.self', 1), value: 'cod_linha', sortable: true, filtrable: true },
        { text: this.$tc('vehicle.self', 1), value: 'numero_ordem', sortable: true, filtrable: true },
        { text: this.$t('relOperationalDeviations.headers.authorized'), value: 'autorizado_extenso', sortable: true, filtrable: true },
      ];
      if (withAssociacao) {
        headers.push({
          text: this.$t('relOperationalDeviations.headers.association'),
          value: 'associacao',
          sortable: true,
          filtrable: true,
        });
      }
      if (withMotivoFora) {
        headers.splice(5, 0, {
          text: this.$t('relOperationalDeviations.headers.outsideFenceReason'),
          value: 'motivo_fora_cerca',
          sortable: true,
          filtrable: true,
        });
      }
      headers.push({
        text: this.$t('relBoarding.header.alerts'),
        value: 'sem_posicoes_janela',
        sortable: true,
        filtrable: false,
      });
      return headers;
    },
    getConfig() {
      apiController.request(apiController.endpoints.getallConfig, {
        onSuccess: (response) => {
          this.config = response.data[0];
          [this.dti, this.dtf] = DateUtils.setDefaultDateFiltersYesterday(
            this.config.hora_inicial_dia_operacao
          );
        },
      });
    },
    populaEmpresas() {
      apiController.request(apiController.endpoints.getallEmpresas, {
        onSuccess: (response) => {
          this.empresas = response.data || [];
        },
      });
    },
    populaUnidades() {
      apiController.request(apiController.endpoints.getallUnidadesativas, {
        onSuccess: (response) => {
          this.unidades = [
            { id_unidade: -1, descricao_unidade: 'Sem Unidade', cod_unidade: 'SU' },
            ...(response.data || []),
          ];
        },
      });
    },
    populaCercas() {
      apiController.request(apiController.endpoints.getallCercasativascomtipo, {
        silent: true,
        onSuccess: (response) => {
          const data = response.data || [];
          let cercas = data;
          if (this.idLinhaSelected && this.idLinhaSelected.length) {
            const idsLinha = new Set(this.idLinhaSelected.map(Number));
            cercas = data.filter((c) => idsLinha.has(Number(c.id_linha)));
          }
          const seen = new Set();
          this.cercasSelect = cercas.filter((c) => {
            if (seen.has(c.id_cerca)) return false;
            seen.add(c.id_cerca);
            return true;
          });
          if (
            this.idCercaSelected != null &&
            !this.cercasSelect.some((c) => c.id_cerca === this.idCercaSelected)
          ) {
            this.idCercaSelected = null;
          }
        },
      });
    },
    onEmpresaSelectChanged() {
      this.idLinhaSelected = [];
      this.populaLinhasSelect(this.idEmpresaSelected);
    },
    populaLinhasSelect(idEmpresa) {
      if (!idEmpresa) {
        this.linhasSelect = [];
        return;
      }
      apiController.request(apiController.endpoints.getLinhasporempresa, {
        params: { id: idEmpresa },
        onSuccess: (response) => {
          this.linhasSelect = response.data || [];
        },
      });
    },
    buildFilterBody() {
      const idLinhas =
        this.idLinhaSelected && this.idLinhaSelected.length
          ? this.idLinhaSelected
          : (this.linhasSelect || []).map((l) => l.id_linha);
      return {
        idEmpresa: this.idEmpresaSelected,
        idUnidades: this.unidadesSelecionadas || [],
        idLinhas,
        dti: DateUtils.getDateSQLFormatted(this.dti),
        dtf: DateUtils.getDateSQLFormatted(this.dtf),
      };
    },
    populaRelatorio() {
      this.$refs.form.validate();
      if (!this.formValidado) return;
      this.activeKpiFilter = null;
      this.filtroBuscaCercaIntervencao = '';
      this.boardingsTableSelectorExpandedKey = null;
      this.clearTableHighlight();
      this.populaResumo();
      this.populaViagensInvalidas();
      this.populaEmbarquesSemViagem();
      this.populaEmbarquesForaCerca();
      this.populaEmbarquesPorCerca();
      this.populaCercasNaoPassadas();
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    populaResumo() {
      this.loadingResumo = true;
      apiController.request(apiController.endpoints.getDesviosoperacionaisresumo, {
        data: this.buildFilterBody(),
        onSuccess: (response) => {
          this.populaCercas();
          const data = response.data || {};
          this.kpis = data.kpis || null;
          this.porAutorizado = data.por_autorizado || [];
          this.porMotivoForaCerca = data.por_motivo_fora_cerca || [];
          this.porMotivoInvalida = data.por_motivo_invalida || [];
          this.loadingResumo = false;
        },
        onError: () => {
          this.kpis = null;
          this.porAutorizado = [];
          this.porMotivoForaCerca = [];
          this.porMotivoInvalida = [];
          this.loadingResumo = false;
        },
      });
    },
    populaViagensInvalidas() {
      this.loadingInvalidas = true;
      apiController.request(apiController.endpoints.getDesviosoperacionaisviagensinvalidas, {
        data: this.buildFilterBody(),
        onSuccess: (response) => {
          this.viagensInvalidas = Array.isArray(response.data) ? response.data : [];
          this.loadingInvalidas = false;
        },
        onError: () => {
          this.viagensInvalidas = [];
          this.loadingInvalidas = false;
        },
      });
    },
    populaEmbarquesForaCerca() {
      this.loadingForaCerca = true;
      apiController.request(apiController.endpoints.getDesviosoperacionaisembarquesforacerca, {
        data: this.buildFilterBody(),
        onSuccess: (response) => {
          this.embarquesForaCerca = Array.isArray(response.data) ? response.data : [];
          this.loadingForaCerca = false;
        },
        onError: () => {
          this.embarquesForaCerca = [];
          this.loadingForaCerca = false;
        },
      });
    },
    populaEmbarquesSemViagem() {
      this.loadingSemViagem = true;
      apiController.request(apiController.endpoints.getDesviosoperacionaisembarquessemviagem, {
        data: this.buildFilterBody(),
        onSuccess: (response) => {
          this.embarquesSemViagem = Array.isArray(response.data) ? response.data : [];
          this.loadingSemViagem = false;
        },
        onError: () => {
          this.embarquesSemViagem = [];
          this.loadingSemViagem = false;
        },
      });
    },
    populaEmbarquesPorCerca() {
      if (!this.dti || !this.dtf || !this.idEmpresaSelected) {
        return;
      }
      this.loadingPorCerca = true;
      const body = { ...this.buildFilterBody(), idCerca: this.idCercaSelected || null };
      apiController.request(apiController.endpoints.getDesviosoperacionaisembarquesporcerca, {
        data: body,
        onSuccess: (response) => {
          this.embarquesPorCerca = Array.isArray(response.data) ? response.data : [];
          this.loadingPorCerca = false;
        },
        onError: () => {
          this.embarquesPorCerca = [];
          this.loadingPorCerca = false;
        },
      });
    },
    populaCercasNaoPassadas() {
      this.loadingCercasNaoPassadas = true;
      apiController.request(apiController.endpoints.getDesviosoperacionaiscercasnaopassadas, {
        data: this.buildFilterBody(),
        onSuccess: (response) => {
          this.cercasNaoPassadas = Array.isArray(response.data) ? response.data : [];
          this.loadingCercasNaoPassadas = false;
        },
        onError: () => {
          this.cercasNaoPassadas = [];
          this.loadingCercasNaoPassadas = false;
        },
      });
    },
    countSemPosicoesJanela() {
      const lists = [this.embarquesSemViagem, this.embarquesForaCerca, this.embarquesPorCerca];
      const seen = new Set();
      let count = 0;
      lists.forEach((items) => {
        (items || []).forEach((item) => {
          if (Number(item.sem_posicoes_janela) !== 1) return;
          const key = item.id_embarque;
          if (key != null && seen.has(key)) return;
          if (key != null) seen.add(key);
          count += 1;
        });
      });
      return count;
    },
    normalizeMotivoInvalida(value) {
      return (value || 'SEM_MOTIVO').trim() || 'SEM_MOTIVO';
    },
    getViagemInvalidacaoSortValue(item) {
      if (item.data_hora_invalidacao) {
        const time = new Date(item.data_hora_invalidacao).getTime();
        if (!Number.isNaN(time)) return time;
      }
      return item.data_hora_invalidacao_extenso || '';
    },
    compareViagemInvalidacaoDesc(a, b) {
      const av = this.getViagemInvalidacaoSortValue(a);
      const bv = this.getViagemInvalidacaoSortValue(b);
      if (typeof av === 'number' && typeof bv === 'number') return bv - av;
      return String(bv).localeCompare(String(av));
    },
    insightHasTableSelector(insight) {
      return ['boardings-not-authorized', 'boardings-wrong-route', 'boardings-no-gps'].includes(insight.key);
    },
    getBoardingTableOptionsForInsight(insight) {
      const tables = [
        {
          subTab: 0,
          items: this.embarquesSemViagem,
          label: this.$t('relOperationalDeviations.tabs.withoutTrip'),
        },
        {
          subTab: 1,
          items: this.embarquesForaCerca,
          label: this.$t('relOperationalDeviations.tabs.outsideFence'),
        },
        {
          subTab: 2,
          items: this.embarquesPorCerca,
          label: this.$t('relOperationalDeviations.tabs.byFence'),
        },
      ];

      return tables
        .map((table) => ({
          ...table,
          count: this.countBoardingMatchesInTable(table.items, insight),
        }))
        .filter((table) => table.count > 0);
    },
    countBoardingMatchesInTable(items, insight) {
      const filters = {
        filtroEmbarqueAutorizado: insight.filtroEmbarqueAutorizado,
        filtroEmbarqueMotivoFora: insight.filtroEmbarqueMotivoFora,
        filtroEmbarqueSemPosicoes: insight.filtroEmbarqueSemPosicoes,
      };
      return (items || []).filter((item) => this.embarqueMatchesInsightFilters(item, filters)).length;
    },
    toggleBoardingsTableSelector(insight) {
      this.boardingsTableSelectorExpandedKey =
        this.boardingsTableSelectorExpandedKey === insight.key ? null : insight.key;
    },
    isBoardingInsightActive(insight) {
      if (this.boardingsTableSelectorExpandedKey === insight.key) return true;
      return this.activeInsightKey === insight.key && this.highlightedTableTarget === 'boardings';
    },
    isBoardingTableSelected(insight, table) {
      return (
        this.activeInsightKey === insight.key &&
        this.highlightedTableTarget === 'boardings' &&
        this.embarqueSubTab === table.subTab
      );
    },
    onBoardingTableSelect(insight, table) {
      if (
        this.activeInsightKey === insight.key &&
        this.highlightedTableTarget === 'boardings' &&
        this.embarqueSubTab === table.subTab
      ) {
        this.clearTableHighlight();
        return;
      }
      this.applyInsightNavigation({
        ...insight,
        key: insight.key,
        title: insight.title,
        embarqueSubTab: table.subTab,
      });
      this.boardingsTableSelectorExpandedKey = insight.key;
    },
    hasActiveEmbarqueInsightFilter() {
      return (
        this.highlightedTableTarget === 'boardings' &&
        !!this.activeInsightKey &&
        (this.filtroEmbarqueAutorizado != null ||
          !!this.filtroEmbarqueMotivoFora ||
          this.filtroEmbarqueSemPosicoes)
      );
    },
    boardingsInsightFilterKey() {
      if (this.filtroEmbarqueAutorizado === 0) return 'nao-autorizado';
      if (this.filtroEmbarqueAutorizado === 'rota_errada') return 'rota-errada';
      if (this.filtroEmbarqueMotivoFora) return this.filtroEmbarqueMotivoFora;
      if (this.filtroEmbarqueSemPosicoes) return 'sem-gps';
      return 'all';
    },
    buildEmbarquesDisplay(items) {
      if (!this.hasActiveEmbarqueInsightFilter()) {
        return items || [];
      }

      const mapped = (items || []).map((item) => {
        const matches = this.embarqueMatchesInsight(item);
        return {
          ...item,
          _sort_embarque_priority: matches ? 0 : 1,
          _sort_embarque_group_key: this.getEmbarqueInsightGroupKey(item),
        };
      });

      return mapped.sort((a, b) => {
        if (a._sort_embarque_priority !== b._sort_embarque_priority) {
          return a._sort_embarque_priority - b._sort_embarque_priority;
        }
        const groupCmp = a._sort_embarque_group_key.localeCompare(b._sort_embarque_group_key);
        if (groupCmp !== 0) return groupCmp;
        return this.compareEmbarqueDesc(a, b);
      });
    },
    getEmbarqueAutorizadoGroupKey(item) {
      if (Number(item.autorizado) === 0) return '0_nao_autorizado';
      if (Number(item.autorizado) === 1) return '1_autorizado';
      if (
        item.autorizado != null &&
        Number(item.autorizado) !== 0 &&
        Number(item.autorizado) !== 1
      ) {
        return '2_rota_errada';
      }
      return '9_outros';
    },
    getEmbarqueInsightGroupKey(item) {
      if (this.filtroEmbarqueMotivoFora) {
        return item.motivo_fora_cerca || 'sem_motivo';
      }
      if (this.filtroEmbarqueSemPosicoes) {
        return Number(item.sem_posicoes_janela) === 1 ? '0_sem_posicoes' : '1_com_posicoes';
      }
      return this.getEmbarqueAutorizadoGroupKey(item);
    },
    getEmbarqueSortValue(item) {
      if (item.dt_embarque) {
        const time = new Date(item.dt_embarque).getTime();
        if (!Number.isNaN(time)) return time;
      }
      return `${item.data_embarque || ''} ${item.hora_embarque || ''}`.trim();
    },
    compareEmbarqueDesc(a, b) {
      const av = this.getEmbarqueSortValue(a);
      const bv = this.getEmbarqueSortValue(b);
      if (typeof av === 'number' && typeof bv === 'number') return bv - av;
      return String(bv).localeCompare(String(av));
    },
    matchesFenceSearch(item, query) {
      const term = (query || '').trim().toLowerCase();
      if (!term) return true;
      const fields = [
        item.nome_cerca,
        item.cod_linha,
        item.descricao_tipo_cerca,
      ];
      return fields.some((field) => field != null && String(field).toLowerCase().includes(term));
    },
    onFenceInsightSelect(fence) {
      const key = `fences-select-${fence.id_cercas_nao_passadas}`;
      if (this.activeInsightKey === key) {
        this.clearTableHighlight();
        return;
      }
      this.applyInsightNavigation({
        key,
        scrollTopic: 'fences',
        tableTarget: 'fences',
        title: this.$t('relOperationalDeviations.insights.fences.selectedFence.title', {
          fence: fence.nome_cerca,
          line: fence.cod_linha,
        }),
        filtroCercaHighlight: {
          id_cerca: fence.id_cerca,
          id_linha: fence.id_linha,
        },
      });
    },
    toggleFenceSelector() {
      this.fenceSelectorExpanded = !this.fenceSelectorExpanded;
    },
    isFenceInsightSelected(fence) {
      return this.activeInsightKey === `fences-select-${fence.id_cercas_nao_passadas}`;
    },
    onBoardingInsightClick(insight) {
      if (this.insightHasTableSelector(insight)) return;
      this.onInsightClick(insight);
    },
    onInsightClick(insight) {
      if (this.activeInsightKey === insight.key) {
        this.clearTableHighlight();
        return;
      }
      this.applyInsightNavigation(insight);
    },
    applyInsightNavigation(insight) {
      this.activeKpiFilter = null;
      this.activeInsightKey = insight.key;
      this.highlightedTableTarget = insight.tableTarget || insight.scrollTopic;
      this.activeTableFilterLabel = insight.title;
      this.filtroMotivoInvalida = insight.filtroMotivoInvalida != null ? insight.filtroMotivoInvalida : null;
      this.filtroEmbarqueAutorizado = insight.filtroEmbarqueAutorizado != null ? insight.filtroEmbarqueAutorizado : null;
      this.filtroEmbarqueMotivoFora = insight.filtroEmbarqueMotivoFora || null;
      this.filtroEmbarqueSemPosicoes = !!insight.filtroEmbarqueSemPosicoes;
      this.filtroCercaHighlight = insight.filtroCercaHighlight || null;

      if (insight.scrollTopic === 'boardings') {
        this.embarqueSubTab = insight.embarqueSubTab != null ? insight.embarqueSubTab : 0;
      }

      this.scrollToTableRef(`${this.highlightedTableTarget}TableCard`);
    },
    clearTableHighlight() {
      this.activeInsightKey = null;
      this.highlightedTableTarget = null;
      this.activeTableFilterLabel = null;
      this.filtroMotivoInvalida = null;
      this.filtroEmbarqueAutorizado = null;
      this.filtroEmbarqueMotivoFora = null;
      this.filtroEmbarqueSemPosicoes = false;
      this.filtroCercaHighlight = null;
      this.fenceSelectorExpanded = false;
      this.boardingsTableSelectorExpandedKey = null;
    },
    embarqueMatchesInsightFilters(item, filters = {}) {
      if (filters.filtroEmbarqueAutorizado === 0) {
        return Number(item.autorizado) === 0;
      }
      if (filters.filtroEmbarqueAutorizado === 'rota_errada') {
        return (
          item.autorizado != null &&
          Number(item.autorizado) !== 0 &&
          Number(item.autorizado) !== 1
        );
      }
      if (filters.filtroEmbarqueMotivoFora) {
        return item.motivo_fora_cerca === filters.filtroEmbarqueMotivoFora;
      }
      if (filters.filtroEmbarqueSemPosicoes) {
        return Number(item.sem_posicoes_janela) === 1;
      }
      return true;
    },
    embarqueMatchesInsight(item) {
      return this.embarqueMatchesInsightFilters(item, {
        filtroEmbarqueAutorizado: this.filtroEmbarqueAutorizado,
        filtroEmbarqueMotivoFora: this.filtroEmbarqueMotivoFora,
        filtroEmbarqueSemPosicoes: this.filtroEmbarqueSemPosicoes,
      });
    },
    insightEmbarqueRowClass(item) {
      if (!this.activeInsightKey || this.highlightedTableTarget !== 'boardings') return '';
      return this.embarqueMatchesInsight(item) ? 'table-row--highlighted' : '';
    },
    viagemMatchesInsight(item) {
      if (this.filtroMotivoInvalida) {
        return this.normalizeMotivoInvalida(item.motivo_viagem_invalida) === this.filtroMotivoInvalida;
      }
      return true;
    },
    insightViagemRowClass(item) {
      if (!this.activeInsightKey || this.highlightedTableTarget !== 'trips') return '';
      return this.viagemMatchesInsight(item) ? 'table-row--highlighted' : '';
    },
    insightFenceRowClass(item) {
      if (!this.activeInsightKey || this.highlightedTableTarget !== 'fences') return '';
      if (this.filtroCercaHighlight) {
        const highlight = this.filtroCercaHighlight;
        if (
          Number(item.id_cerca) === Number(highlight.id_cerca) &&
          Number(item.id_linha) === Number(highlight.id_linha)
        ) {
          return 'table-row--highlighted';
        }
        return '';
      }
      return Number(item.qtd_nao_passadas) > 0 ? 'table-row--highlighted' : '';
    },
    scrollToTableRef(refName) {
      this.$nextTick(() => {
        const ref = this.$refs[refName];
        const el = ref && ref.$el ? ref.$el : ref;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    onKpiClick(card) {
      if (this.activeKpiFilter === card.key) {
        this.activeKpiFilter = null;
        this.clearTableHighlight();
        return;
      }
      this.clearTableHighlight();
      this.activeKpiFilter = card.key;
      if (card.topic) {
        const targetTopic = card.scrollTopic || card.topic;
        this.navigateToTopic(targetTopic, card.embarqueSubTab != null ? card.embarqueSubTab : 0);
      }
    },
    navigateToTopic(topic, embarqueSubTab = 0) {
      if (topic === 'boardings') {
        this.embarqueSubTab = embarqueSubTab;
      }
      this.$nextTick(() => {
        const refMap = {
          boardings: 'sectionBoardings',
          trips: 'sectionTrips',
          fences: 'sectionFences',
        };
        const refName = refMap[topic];
        const el = refName ? this.$refs[refName] : null;
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    motivoForaCercaLabel(codigo) {
      if (codigo === 'sem_cerca') return this.$t('relOperationalDeviations.outsideFenceReasons.noFence');
      if (codigo === 'cerca_fora_rota') return this.$t('relOperationalDeviations.outsideFenceReasons.offRoute');
      return codigo || '';
    },
    motivoInvalidaLabel(codigo) {
      const key = String(codigo || '').trim();
      if (!key) return '';
      const i18nKey = `relOperationalDeviations.invalidReasons.${key}`;
      if (this.$te(i18nKey)) return this.$t(i18nKey);
      return key;
    },
    autorizadoLabel(item) {
      if (item.autorizado == 1) return this.$t('relOperationalDeviations.authorizationStatus.authorized');
      if (item.autorizado == 0) return this.$t('relOperationalDeviations.authorizationStatus.notAuthorized');
      if (item.autorizado != null) return this.$t('relOperationalDeviations.authorizationStatus.wrongRoute');
      return item.autorizado_extenso || '';
    },
    formatDateTimeShort(value) {
      if (!value) return '';
      const parts = String(value).trim().split(/\s+/);
      if (parts.length < 2) return value;
      const dateParts = parts[0].split('/');
      const time = parts[1].substring(0, 5);
      if (dateParts.length < 2) return value;
      return `${dateParts[0]}/${dateParts[1]} ${time}`;
    },
    associacaoLabel(codigo) {
      if (codigo === 'sem_id_viagem') return this.$t('relOperationalDeviations.association.withoutTrip');
      if (codigo === 'viagem_invalida') return this.$t('relOperationalDeviations.association.invalidTrip');
      if (codigo === 'viagem_valida') return this.$t('relOperationalDeviations.association.validTrip');
      return codigo || '';
    },
    abrirRelatorioViagem(item) {
      this.editedItem = Object.assign({}, item);
      this.relatorioKey = this.relatorioKey === 0 ? 1 : 0;
      this.dialogRelatorio = true;
    },
    closeRelatorio() {
      this.editedItem = {};
      this.dialogRelatorio = false;
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    resolvePdfEl(ref) {
      if (!ref) return null;
      const target = Array.isArray(ref) ? ref[0] : ref;
      if (!target) return null;
      return target.$el || target;
    },
    html2canvasOptions(el) {
      const width = Math.max(el.scrollWidth, el.clientWidth, 1200);
      const height = Math.max(el.scrollHeight, el.clientHeight, 1);
      // Evita estourar o limite de canvas do browser em seções grandes
      const maxEdge = 8000;
      const scale = Math.min(1.5, maxEdge / Math.max(width, height));
      return {
        scale: Math.max(1, scale),
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        width,
        height,
        windowWidth: width,
        windowHeight: height,
        ignoreElements: (node) =>
          node.classList && node.classList.contains('pdf-export-overlay'),
      };
    },
    addCanvasPagesToPdf(doc, canvas, { addPageBefore = false, title = '' } = {}) {
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const titleSpace = title ? 8 : 0;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2 - titleSpace;
      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 0.85);

      let heightLeft = imgHeight;
      let y = margin + titleSpace;

      if (addPageBefore) doc.addPage();
      if (title) {
        doc.setFontSize(12);
        doc.setTextColor(40);
        doc.text(String(title), margin, margin + 4);
      }
      doc.addImage(imgData, 'JPEG', margin, y, imgWidth, imgHeight);
      heightLeft -= usableHeight;

      while (heightLeft > 0) {
        y = margin - (imgHeight - heightLeft);
        doc.addPage();
        doc.addImage(imgData, 'JPEG', margin, y, imgWidth, imgHeight);
        heightLeft -= usableHeight;
      }
    },
    async captureElToPdf(doc, ref, { addPageBefore = true, title = '' } = {}) {
      const el = this.resolvePdfEl(ref);
      if (!el || typeof el.getBoundingClientRect !== 'function') return false;
      el.scrollIntoView({ behavior: 'auto', block: 'start' });
      await this.$nextTick();
      await this.sleep(400);
      const canvas = await html2canvas(el, this.html2canvasOptions(el));
      this.addCanvasPagesToPdf(doc, canvas, { addPageBefore, title });
      return true;
    },
    writePdfCoverPage(doc) {
      const margin = 14;
      doc.setFontSize(18);
      doc.setTextColor(25, 118, 210);
      doc.text(this.$t('relOperationalDeviations.exportPdfTitle'), margin, 24);

      doc.setFontSize(11);
      doc.setTextColor(80);
      const periodo = `${DateUtils.getDateSQLFormatted(this.dti) || ''} — ${DateUtils.getDateSQLFormatted(this.dtf) || ''}`;
      doc.text(`${this.$t('relOperationalDeviations.exportPdfPeriod')}: ${periodo}`, margin, 38);

      if (this.empresaLabel) {
        doc.text(`${this.$tc('company.self', 1)}: ${this.empresaLabel}`, margin, 46);
      }

      const emitted = new Date();
      const emittedStr = emitted.toLocaleString(this.$i18n.locale || 'pt-BR');
      doc.text(`${this.$t('relOperationalDeviations.exportPdfEmittedAt')}: ${emittedStr}`, margin, 54);

      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text(this.$t('relOperationalDeviations.exportPdfHint'), margin, 70, { maxWidth: 270 });
    },
    async exportarAnalisePdf() {
      if (!this.hasKpis || this.loadingPdf) return;
      this.loadingPdf = true;

      try {
        await this.$nextTick();
        await this.sleep(300);

        const doc = new jsPDF({
          orientation: 'landscape',
          unit: 'mm',
          format: 'a4',
          compress: true,
        });
        this.writePdfCoverPage(doc);

        const sections = [
          { ref: this.$refs.sectionBoardings, title: this.$t('relOperationalDeviations.topics.boardings') },
          { ref: this.$refs.sectionTrips, title: this.$t('relOperationalDeviations.topics.trips') },
          { ref: this.$refs.sectionFences, title: this.$t('relOperationalDeviations.topics.fences') },
        ];

        let captured = 0;
        for (const section of sections) {
          const ok = await this.captureElToPdf(doc, section.ref, {
            addPageBefore: true,
            title: section.title,
          });
          if (ok) captured += 1;
        }

        if (!captured) {
          throw new Error('Nenhuma seção encontrada para exportar');
        }

        const stamp = new Date().toISOString().replace(/[:.]/g, '-');
        doc.save(`desviosoperacionais_${stamp}.pdf`);
      } catch (err) {
        console.error('PDF export failed', err);
        // eslint-disable-next-line no-alert
        window.alert(this.$t('relOperationalDeviations.exportPdfError'));
      } finally {
        this.loadingPdf = false;
      }
    },
  },
};
</script>

<style>
.toolbar__container {
  height: fit-content;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0px 5px 5px grey;
  padding: 20px 15px;
}
.kpi-cards-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
}
.topic-section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #4a4a4a;
  line-height: 1.4;
}
.kpi-card {
  flex: 1 1 0;
  min-width: 0;
  cursor: pointer;
}
.kpi-card__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.invalid-reason-legend {
  width: 100%;
}
.invalid-reason-legend__item {
  font-size: 13px;
  line-height: 1.45;
  color: #424242;
  margin-bottom: 4px;
}
.invalid-reason-legend__item:last-child {
  margin-bottom: 0;
}
.insights-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.insight-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.insight-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
.insight-item--error {
  border-left: 4px solid #c62828;
  background-color: rgba(198, 40, 40, 0.04);
}
.insight-item--warning {
  border-left: 4px solid #ef6c00;
  background-color: rgba(239, 108, 0, 0.04);
}
.insight-item--info {
  border-left: 4px solid #1565c0;
  background-color: rgba(21, 101, 192, 0.04);
}
.insight-item__icon {
  flex-shrink: 0;
  margin-top: 2px;
}
.insight-item__content {
  flex: 1;
  min-width: 0;
}
.insight-item--expandable {
  align-items: flex-start;
  cursor: default;
}
.insight-item__body {
  flex: 1;
  min-width: 0;
}
.insight-item__icon {
  cursor: pointer;
}
.insight-item__icon--clickable {
  cursor: pointer;
}
.insight-item__header {
  cursor: pointer;
}
.insight-item__chevron-btn {
  flex-shrink: 0;
  margin-top: 2px;
}
.fence-insight-selector {
  margin-top: 8px;
}
.fence-insight-search {
  margin-bottom: 8px;
}
.fence-insight-empty {
  padding: 12px 4px;
}
.fence-insight-list {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  max-height: 240px;
  overflow-y: auto;
}
.fence-insight-item {
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.fence-insight-item:last-child {
  border-bottom: none;
}
.fence-insight-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
.fence-insight-item--active {
  background-color: rgba(21, 101, 192, 0.08);
  box-shadow: inset 3px 0 0 #1565c0;
}
.insight-item--active {
  box-shadow: inset 0 0 0 2px #1565c0;
}
.table-card--highlighted {
  box-shadow: 0 0 0 2px #1565c0 !important;
}
.table-row--highlighted,
.table-row--highlighted td {
  background-color: rgba(21, 101, 192, 0.12) !important;
}
.insight-item__action {
  color: #616161;
}
.insight-item__chevron {
  flex-shrink: 0;
  margin-top: 4px;
}
.pdf-export-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
}
</style>
